<script setup lang="ts">
import DeleteIcon from '~/assets/icon/delete.svg'
import CloseIcon from '~/assets/icon/close.svg'
import StarIcon from '~/assets/icon/star.svg'
import ArrowDownIcon from '~/assets/icon/arrow-down.svg'
import { normalizeLocaleParam, type LocaleCode } from '@packages/i18n'
import { formatScoreValue } from '~/utils/formatters'
import GlobalIcon from '~/assets/icon/global.svg'
import ServiceIcon from '~/assets/icon/service.svg'
import DownloadIcon from '~/assets/icon/download.svg'
import SearchIcon from '~/assets/icon/search.svg'
import { FALLBACK_DOWNLOAD_LINKS } from '~/api/download'
import type { SearchHistoryItem, TraderListItem } from '~/types/home'
import { calculateTopNavMoveCount } from '~/utils/topNavOverflow'
import { resolveTopNavActiveKey } from '~/utils/topNavActive'
import { getRankCategories, type RankCategoryItem } from '~/server/ranking'

type NavChildItem = {
  label: string
  href: string
  key: string
  hasBottom?: boolean
}

type NavItem = {
  key: string
  label: string
  href: string
  active?: boolean
  hasArrow?: boolean
  children?: NavChildItem[]
}

type UserMenuItem = {
  label: string
  href: string
  action?: 'logout'
  danger?: boolean
  key?: string
}

const { locales, localePath, switchLocalePath, t, locale } = useAppI18n()
const matomo = useMatomo()
const { data: rankCategoriesData } = await useAsyncData('best-exchanges-ranking-categories', async () => {
  const rankingData = await getRankCategories()
  return rankingData.data
})

/** 头部排行榜下拉菜单数据，优先使用接口返回的 englishName 作为路由 slug。 */
const rankingNavChildren = computed<NavChildItem[]>(() =>
  (rankCategoriesData.value?.items ?? []).map((item: RankCategoryItem) => {
    const rankingSlug = item.englishName || item.name

    return {
      label: item.name,
      key: item.code,
      href: localePath({ name: 'best-exchanges-slug', params: { slug: rankingSlug } }),
    }
  }),
)

const BASE_NAV_ITEMS: NavItem[] = [
  { key: 'home', label: t('82520'), href: localePath({ name: 'home' }) },
  {
    key: 'ranking',
    label: t('82477'),
    hasArrow: true,
    href: '',
  },
  // { key: 'disclosure', label: t('82879'), href: `/${locale}/disclosure/` }, //暂未放开
  { key: 'reviews', label: t('82430'), href: localePath({ name: 'reviews' }) },
  {
    key: 'survey',
    label: t('82484'),
    href: localePath({
      name: 'inspections-category',
      params: {
        category: 'all',
      },
    }),
  },

  {
    key: 'more',
    label: t('82485'),
    href: '#',
    hasArrow: true,

    children: [
      { label: t('82488'), href: localePath({ name: 'market' }), key: '市场行情' },
      { label: t('82501'), href: localePath({ name: 'data' }), key: '市场数据' },
      // { label: '交易量排名', href: '#', hasBottom: true },
      { label: t('82486'), href: localePath({ name: 'about' }), key: '关于我们' },
    ],
  },
]

const afTypeMap = new Map([
  ['home', '首页'],
  ['ranking', '排行榜'],
  ['reviews', '反馈'],
  ['survey', '实勘'],
  ['more', '更多'],
  ['usercener', '个人中心'],
  ['Search', '搜索'],
  ['Language', '语言选择'],
  ['Support', '联系我们'],
  ['Download', '下载APP'],
])

const actionButtons = [
  {
    label: 'Search',
    icon: 'https://resources1.interface003.com/web30/2026/05/19/e1e89d8f-6c86-4291-b3a5-df523f7bacf7.svg',
  },
  {
    label: 'Language',
    icon: 'https://resources1.interface003.com/web30/2026/05/19/2b84848a-07ea-46a5-bc4a-54e285f859cb.svg',
  },
  {
    label: 'Support',
    icon: 'https://resources1.interface003.com/web30/2026/05/19/e2f84b26-8dcc-450d-b288-57169a154466.svg',
  },
  {
    label: 'Download',
    icon: 'https://resources1.interface003.com/web30/2026/05/19/1d6feda3-c580-4716-8584-39571ad7a5f6.png',
  },
] as const

const props = withDefaults(
  defineProps<{
    alwaysExpandSearch?: boolean
    forceSolidBackground?: boolean
    special?: boolean
    ultra?: boolean
  }>(),
  {
    ultra: false,
  },
)

const route = useRoute()

/** 全局共享下载地址，SSR 预取。 */
const { data: appDownloadLinks } = await useAppDownloadLinks()
const downloadQrUrl = computed(
  () =>
    appDownloadLinks.value?.data?.h5down ||
    (appDownloadLinks.value as unknown as { h5down?: string } | null)?.h5down ||
    FALLBACK_DOWNLOAD_LINKS.data.h5down,
)
const appStore = useAppStore()
const { clearLoginCookies } = useLogin()
const userIdCookie = useAppCookie<string | number | null>('userid')
const LANGUAGE_PROMPT_DISMISS_KEY = 'wikibit-pc-language-prompt-dismissed-date'
const browserLocale = ref<LocaleCode | null>(null)
const browserLocalePromptText = ref('')
const browserLocalePromptConfirmText = ref('')
const showLanguagePrompt = ref(false)
const isLanguagePopoverVisible = ref(false)
const isDownloadPopoverVisible = ref(false)
const DOWNLOAD_POPOVER_HIDE_DELAY = 120
let downloadPopoverHideTimer: ReturnType<typeof setTimeout> | null = null
const storedUserAvatar = ref('')
const isAuthStateHydrated = ref(false)

const topNavContentRef = ref<HTMLElement | null>(null)
const topNavLeftRef = ref<HTMLElement | null>(null)
const navActionRef = ref<HTMLElement | null>(null)
const navMeasureRef = ref<HTMLElement | null>(null)
const userMenuRef = ref<HTMLElement | null>(null)
const isUserMenuOpen = ref(false)
const showLogoutConfirmDialog = ref(false)
// 允许布局或页面显式传入交易所页面状态，未传时继续按当前路由自动判断。
const resolvedIsExchangeRoute = computed(() => props.special)
const isNewsDetailRoute = computed(() => route.path.includes('/news/'))
const showExchangeTransparentLogo = computed(() => resolvedIsExchangeRoute.value && scrollProgress.value < 1)
const languagePromptText = computed(() => browserLocalePromptText.value || t('82388'))
const languagePromptConfirmText = computed(() => browserLocalePromptConfirmText.value || t('82389'))

const normalizeBrowserLocale = (value: string): LocaleCode | null => {
  const normalizedValue = value.trim().replaceAll('_', '-').toLowerCase()

  if (!normalizedValue) {
    return null
  }

  const candidate =
    normalizedValue !== 'zh-cn' && normalizedValue.includes('-')
      ? (normalizedValue.split('-')[0] ?? '')
      : normalizedValue

  return normalizeLocaleParam(candidate)
}

const getNaturalDayKey = () => {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = `${currentDate.getMonth() + 1}`.padStart(2, '0')
  const day = `${currentDate.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const hideLanguagePrompt = () => {
  showLanguagePrompt.value = false
}

const syncBrowserLocalePromptMessages = async (targetLocale: LocaleCode | null) => {
  if (!targetLocale) {
    browserLocalePromptText.value = ''
    browserLocalePromptConfirmText.value = ''
    return
  }

  const [promptText, promptConfirmText] = await Promise.all([
    getSeoLocaleMessage(targetLocale, '82388'),
    getSeoLocaleMessage(targetLocale, '82389'),
  ])

  browserLocalePromptText.value = promptText
  browserLocalePromptConfirmText.value = promptConfirmText
}

const handleCloseLanguagePrompt = () => {
  matomo.trackEvent('home_language_confirm', {
    af_type: '关闭',
  })
  if (import.meta.client) {
    window.localStorage.setItem(LANGUAGE_PROMPT_DISMISS_KEY, getNaturalDayKey())
  }
  hideLanguagePrompt()
}

const handleSwitchToBrowserLocale = () => {
  matomo.trackEvent('home_language_confirm', {
    af_type: '切换',
  })
  if (!browserLocale.value) {
    return
  }

  if (import.meta.client) {
    window.localStorage.setItem(LANGUAGE_PROMPT_DISMISS_KEY, getNaturalDayKey())
  }
  hideLanguagePrompt()
  const targetUrl = switchLocalePath(browserLocale.value)

  if (import.meta.client) {
    window.location.href = targetUrl
  }
}

const handleLanguageItemClick = (code: string) => {
  matomo.trackEvent('home_language', {
    af_type: code,
  })
  hideLanguagePrompt()
}

const openLoginModal = () => {
  appStore.setLoginShow(true)
}

// SSR 缓存页面统一输出登录按钮，挂载后再按 userid Cookie 切换，避免 hydration 结构不一致。
const isLoggedIn = computed(() => isAuthStateHydrated.value && Boolean(String(userIdCookie.value ?? '').trim()))
const userDisplayName = computed(() => `${appStore.user?.nick || appStore.user?.nickName || ''}`.trim() || 'My Account')
const userInitial = computed(() => userDisplayName.value.slice(0, 1).toUpperCase() || 'W')
const resolvedUserAvatar = computed(() => storedUserAvatar.value.trim())

const userMenuItems = computed<UserMenuItem[]>(() => [
  {
    label: t('82392'),
    href: localePath('/account/posts'),
    key: '我的发布',
  },
  {
    label: t('84550'),
    href: localePath('/account/following'),
    key: '我的监控',
  },
  {
    label: t('82394'),
    href: localePath('/account'),
    key: '个人设置',
  },
  {
    label: t('82395'),
    href: 'javascript:void(0)',
    key: '退出登录',
    action: 'logout',
    danger: true,
  },
])

const closeUserMenu = () => {
  isUserMenuOpen.value = false
}

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value
}

const openUserMenu = () => {
  isUserMenuOpen.value = true
}

const closeLogoutConfirmDialog = (text?: string) => {
  if (text) {
    matomo.trackEvent('home_logout_pop', {
      af_type: text,
    })
  }

  showLogoutConfirmDialog.value = false
}

const openLogoutConfirmDialog = () => {
  showLogoutConfirmDialog.value = true
}

const confirmLogout = () => {
  matomo.trackEvent('home_logout_pop', {
    af_type: '取消',
  })
  clearLoginCookies()
  closeLogoutConfirmDialog()
  if (route.path.includes('/account')) {
    navigateTo(localePath('/'), { external: true })
  }
}

const handleUserMenuItemClick = (event: MouseEvent, item: UserMenuItem) => {
  matomo.trackEvent('home_profile', {
    af_type: item.key,
  })
  if (item.action === 'logout') {
    event.preventDefault()
    openLogoutConfirmDialog()
    closeUserMenu()
    return
  }

  closeUserMenu()
}

const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof Node)) {
    return
  }

  if (userMenuRef.value?.contains(target)) {
    return
  }

  closeUserMenu()
}

const handleSupportClick = async () => {
  await navigateTo(localePath({ name: 'contact' }), {
    open: {
      target: '_blank',
    },
  })
}

const handleDownloadClick = async () => {
  await navigateTo(localePath('/download'), {
    open: {
      target: '_blank',
    },
  })
}

const handleLanguageButtonEnter = () => {
  isLanguagePopoverVisible.value = true
}

const handleLanguageButtonLeave = () => {
  isLanguagePopoverVisible.value = false
}

const clearDownloadPopoverHideTimer = () => {
  if (downloadPopoverHideTimer) {
    clearTimeout(downloadPopoverHideTimer)
    downloadPopoverHideTimer = null
  }
}

const handleDownloadButtonEnter = () => {
  clearDownloadPopoverHideTimer()
  isDownloadPopoverVisible.value = true
}

const handleDownloadButtonLeave = () => {
  clearDownloadPopoverHideTimer()
  downloadPopoverHideTimer = setTimeout(() => {
    isDownloadPopoverVisible.value = false
    downloadPopoverHideTimer = null
  }, DOWNLOAD_POPOVER_HIDE_DELAY)
}

/** 从本地用户快照恢复头像，兼容刷新后 Store 尚未回填的场景。 */
const readStoredUserAvatar = () => {
  if (import.meta.server) {
    return ''
  }

  try {
    const rawStoredUser = window.localStorage.getItem('shared-app-user')

    if (!rawStoredUser) {
      return ''
    }

    const parsedStoredUser = JSON.parse(rawStoredUser) as { avatar?: unknown }
    return typeof parsedStoredUser.avatar === 'string' ? parsedStoredUser.avatar.trim() : ''
  } catch {
    return ''
  }
}

const minMoveCount = computed(() => (['fr', 'de', 'pt', 'tr', 'vi'].includes(locale) ? 1 : 0))
const navItemsToMoveIntoMore = ref(minMoveCount.value)
const activeTopNavKey = computed(() => resolveTopNavActiveKey(route.path))

const navItems = computed<NavItem[]>(() => {
  const items = BASE_NAV_ITEMS.map((item) => ({
    ...item,
    active: item.key === activeTopNavKey.value,
    children:
      item.key === 'ranking'
        ? rankingNavChildren.value.map((child) => ({ ...child }))
        : item.children
          ? item.children.map((child) => ({ ...child }))
          : undefined,
  }))

  if (navItemsToMoveIntoMore.value <= 0) {
    return items
  }

  const moreIndex = items.findIndex((item) => item.key === 'more')

  if (moreIndex <= 0) {
    return items
  }

  const moreItem = items[moreIndex]

  if (!moreItem) {
    return items
  }

  const movableItems = items.slice(Math.max(0, moreIndex - navItemsToMoveIntoMore.value), moreIndex)

  if (movableItems.length === 0) {
    return items
  }

  const prependedChildren = movableItems.map((item, index) => ({
    label: item.label,
    href: item.href,
    hasBottom: index === movableItems.length - 1,
  }))

  const nextChildren = [...prependedChildren, ...(moreItem.children ?? [])]

  items.splice(moreIndex - movableItems.length, movableItems.length)

  const updatedMoreIndex = items.findIndex((item) => item.key === 'more')
  if (updatedMoreIndex >= 0) {
    items[updatedMoreIndex] = {
      ...items[updatedMoreIndex],
      children: nextChildren,
    }
  }

  return items
})

// 搜索区域可见性状态
const { isSearchBoxVisible } = useSearchBoxVisibility()
const scrollProgress = ref(0)
const showNavSearchBar = computed(
  () => props.alwaysExpandSearch || !isSearchBoxVisible.value || scrollProgress.value >= 1,
)
const isNavSearchClicked = ref(false)
const isCurrentSearchRoute = computed(() => String(route.name || '') === 'search' || route.path.endsWith('/search'))

// 搜索页顶部搜索提交只更新当前页查询参数，由搜索页监听后局部刷新结果。
const handleNavSearchSubmitByRoute = async (keyword: string) => {
  const normalizedKeyword = keyword.trim()

  if (!isCurrentSearchRoute.value) {
    await navigateTo(localePath(`/search?keyword=${encodeURIComponent(normalizedKeyword)}`), {
      open: {
        target: '_blank',
      },
    })
    return
  }

  await navigateTo(localePath(`/search?keyword=${encodeURIComponent(normalizedKeyword)}`), {
    replace: true,
  })
}

const {
  searchQuery: navSearchQuery,
  isFocused: isNavSearchFocused,
  searchHistory: navSearchHistory,
  searchDefaultData: navSearchDefaultData,
  popularTraders: navPopularRecommendations,
  hasSearched: hasNavSearched,
  fetchSearchSuggest: fetchNavSearchSuggest,
  handleSearch: handleNavSearch,
  onBlur: handleBaseNavSearchBlur,
  selectHistoryItem: selectNavSearchHistory,
  selectTraderItem: selectNavTraderItem,
  deleteHistoryItem: deleteNavSearchHistory,
  clearAllHistory: clearNavSearchHistory,
  initSearchHistory,
  handleFocus: handleNavSearchFocus,
} = useHomeSearch({
  immediateDefault: false,
  onSearchSubmit: handleNavSearchSubmitByRoute,
})

// URL 中的搜索关键词需要在头部搜索框回显，并随查询参数变化同步更新。
watch(
  () => route.query.keyword,
  (keyword) => {
    const firstKeyword = Array.isArray(keyword) ? keyword[0] : keyword
    navSearchQuery.value = typeof firstKeyword === 'string' ? firstKeyword.trim() : ''
  },
  { immediate: true },
)

// 导航搜索框在吸附态默认展开，在首页顶部则通过 click/focus 临时展开。
const isNavSearchExpanded = computed(
  () =>
    showNavSearchBar.value || isNavSearchClicked.value || isNavSearchFocused.value || navSearchQuery.value.length > 0,
)
// 搜索页同样需要展示顶部搜索下拉，便于用户直接切换关键词或查看建议。
const showNavSearchDropdown = computed(() => isNavSearchExpanded.value && isNavSearchFocused.value)

const hasNavSearchHistory = computed(() => navSearchHistory.value.length > 0)
const navSearchKeyword = computed(() => navSearchQuery.value.trim())
const showNavSearchEmpty = computed(
  () => navSearchKeyword.value.length > 0 && hasNavSearched.value && navPopularRecommendations.value.length === 0,
)

// === 历史标签两行折叠逻辑（基于宽度累加计算） ===
const isNavHistoryExpanded = ref(false)
const hasNavMoreThanTwoLines = ref(false)
const maxNavVisibleIndex = ref(9999)

// tag 样式常量：padding 左右各 12px、gap 12px、logo 宽 20px + gap 4px
const TAG_PADDING = 24
const TAG_GAP = 12
const TAG_LOGO_EXTRA = 24 // logo(20px) + gap(4px)
const TAG_DELETE_EXTRA = 18 // delete(14px) + gap(4px)
const EXPAND_BTN_WIDTH = 30 // 展开按钮宽度
const NAV_DROPDOWN_CONTENT_WIDTH = 416 - 64 // 下拉框宽度 416px - padding 左右各 32px = 352px

let _measureCanvas: HTMLCanvasElement | null = null
const getMeasureCtx = () => {
  if (!_measureCanvas) {
    _measureCanvas = document.createElement('canvas')
  }
  const ctx = _measureCanvas.getContext('2d')
  if (ctx) {
    ctx.font = '400 14px Outfit, sans-serif'
  }
  return ctx
}

const calcNavHistoryLayout = () => {
  const history = navSearchHistory.value
  if (!history || history.length === 0) {
    hasNavMoreThanTwoLines.value = false
    maxNavVisibleIndex.value = 9999
    return
  }

  const ctx = getMeasureCtx()
  if (!ctx) return

  const containerWidth = NAV_DROPDOWN_CONTENT_WIDTH
  let currentLineWidth = 0
  let lineCount = 1
  let thirdLineIndex = -1

  for (let i = 0; i < history.length; i++) {
    const item = history[i]
    const textWidth = ctx.measureText(item.label || '').width
    const logoWidth = item.logo ? TAG_LOGO_EXTRA : 0
    // tag width includes text, padding, logo (if any), and delete button with its flex gap
    const tagWidth = Math.ceil(textWidth + TAG_PADDING + logoWidth + TAG_DELETE_EXTRA + 2) // +2px for safety margin

    // 如果当前行已有元素，需要加上 gap
    const widthNeeded = currentLineWidth > 0 ? TAG_GAP + tagWidth : tagWidth

    let effectiveContainerWidth = containerWidth
    // 为第二行预留展开按钮的宽度以及额外的 20px
    if (lineCount === 2 && i < history.length - 1) {
      effectiveContainerWidth = containerWidth - EXPAND_BTN_WIDTH - 20
    }

    if (currentLineWidth + widthNeeded > effectiveContainerWidth && currentLineWidth > 0) {
      // 换行
      lineCount++
      if (lineCount > 2) {
        thirdLineIndex = i
        break
      }
      currentLineWidth = tagWidth
    } else {
      currentLineWidth += widthNeeded
    }
  }

  if (thirdLineIndex !== -1) {
    hasNavMoreThanTwoLines.value = true
    maxNavVisibleIndex.value = thirdLineIndex
  } else {
    hasNavMoreThanTwoLines.value = false
    maxNavVisibleIndex.value = 9999
  }
}

watch(
  () => navSearchHistory.value,
  () => {
    calcNavHistoryLayout()
  },
  { deep: true, immediate: true },
)

const handleClearNavSearchHistory = () => {
  clearNavSearchHistory()
}

const handleDeleteNavSearchHistory = (index: number) => {
  deleteNavSearchHistory(index)
}

const handleSelectNavSearchHistory = async (history: Readonly<SearchHistoryItem>) => {
  await selectNavSearchHistory(history)
  isNavSearchFocused.value = false
}

const handleSelectNavRecommendation = async (recommendation: Readonly<TraderListItem>) => {
  isNavSearchFocused.value = false
  await selectNavTraderItem(recommendation)
}

// 搜索框展开后等待 DOM 更新完成，再聚焦输入框以显示闪烁光标。
const focusNavSearchInput = async () => {
  await nextTick()

  document.getElementById('navSearchInputRef')?.focus()
}

// 点击导航搜索壳时，同步展开并进入输入聚焦态。
const handleNavSearchExpand = () => {
  homeTop('Search')
  if (navSearchCollapseTimer) {
    clearTimeout(navSearchCollapseTimer)
    navSearchCollapseTimer = null
  }

  isNavSearchClicked.value = true
  handleNavSearchFocus()
  focusNavSearchInput()
}

// 导航搜索提交时，空关键词只聚焦输入框，避免首页顶部收起态直接跳转搜索页。
const handleNavSearchSubmit = async () => {
  if (navSearchKeyword.value.length === 0) {
    handleNavSearchExpand()
    return
  }

  await handleNavSearch()
  // 提交后关闭下拉，避免搜索建议被清空时仍显示空面板。
  isNavSearchFocused.value = false
  document.getElementById('navSearchInputRef')?.blur()
}

let navSearchCollapseTimer: ReturnType<typeof setTimeout> | null = null
let navSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null

// 输入框失焦后，如果没有关键词则回到顶部收起态。
const handleNavSearchBlur = () => {
  handleBaseNavSearchBlur()

  if (navSearchCollapseTimer) {
    clearTimeout(navSearchCollapseTimer)
  }

  navSearchCollapseTimer = setTimeout(() => {
    if (navSearchKeyword.value.length === 0) {
      isNavSearchClicked.value = false
    }
  }, 220)
}

watch(navSearchQuery, (value) => {
  if (navSearchDebounceTimer) {
    clearTimeout(navSearchDebounceTimer)
  }

  navSearchDebounceTimer = setTimeout(() => {
    void fetchNavSearchSuggest(value.trim())
  }, 250)
})

watch(
  () => [isLoggedIn.value, appStore.user?.avatar],
  () => {
    storedUserAvatar.value = readStoredUserAvatar()
  },
  { immediate: true },
)

// 页面滚动监听状态与渐变进度逻辑（在 70px 滚动距离内渐变）
function handleScroll() {
  scrollProgress.value = Math.min(window.scrollY / 70, 1)
}

let navResizeObserver: ResizeObserver | null = null
let navMeasureFrame: number | null = null

/**
 * 读取完整菜单的自然宽度，并根据当前剩余空间更新“更多”收纳数量。
 */
const measureNavOverflow = () => {
  const contentElement = topNavContentRef.value
  const leftElement = topNavLeftRef.value
  const actionElement = navActionRef.value
  const measureElement = navMeasureRef.value

  if (!contentElement || !leftElement || !actionElement || !measureElement) {
    return
  }

  const itemWidths = Array.from(measureElement.querySelectorAll<HTMLElement>('[data-nav-measure-item]')).map(
    (item) => item.getBoundingClientRect().width,
  )

  if (itemWidths.length !== BASE_NAV_ITEMS.length) {
    return
  }

  const measuredGap = Number.parseFloat(window.getComputedStyle(measureElement).columnGap)
  const availableWidth =
    contentElement.getBoundingClientRect().width -
    leftElement.getBoundingClientRect().width -
    actionElement.getBoundingClientRect().width
  const calculatedMoveCount = calculateTopNavMoveCount(
    itemWidths,
    Number.isFinite(measuredGap) ? measuredGap : 0,
    availableWidth,
  )
  navItemsToMoveIntoMore.value = Math.max(minMoveCount.value, calculatedMoveCount)
}

/**
 * 将同一帧内的连续尺寸变化合并为一次菜单测量。
 */
const scheduleNavOverflowMeasure = () => {
  if (navMeasureFrame !== null) {
    return
  }

  navMeasureFrame = window.requestAnimationFrame(() => {
    navMeasureFrame = null
    measureNavOverflow()
  })
}

const homeTop = (item: string) => {
  matomo.trackEvent('search', {
    af_source: '顶部',
  })
  matomo.trackEvent('home_top', {
    af_type: afTypeMap.get(item),
  })
}

const homeRank = (item: any) => {
  if (['市场行情', '市场数据', '关于我们'].includes(item.type)) {
    return matomo.trackEvent('home_more', {
      af_type: item,
    })
  }
  matomo.trackEvent('home_rank', {
    af_type: item,
  })
}

onMounted(async () => {
  isAuthStateHydrated.value = true
  const serverInjectedAvatar = document.querySelector('[data-auth-avatar]')?.getAttribute('data-auth-avatar-src')
  if (serverInjectedAvatar) {
    storedUserAvatar.value = serverInjectedAvatar
  }

  initSearchHistory()
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll() // 初始化执行一次，以防页面刷新时已经处于滚动状态
  document.addEventListener('click', handleDocumentClick, { passive: true })

  navResizeObserver = new ResizeObserver(scheduleNavOverflowMeasure)
  const observedElements = [topNavContentRef.value, topNavLeftRef.value, navActionRef.value, navMeasureRef.value]
  observedElements.forEach((element) => {
    if (element) {
      navResizeObserver?.observe(element)
    }
  })
  scheduleNavOverflowMeasure()

  const resolvedBrowserLocale = normalizeBrowserLocale(navigator.language)
  browserLocale.value = resolvedBrowserLocale
  await syncBrowserLocalePromptMessages(resolvedBrowserLocale)

  const dismissedDate = window.localStorage.getItem(LANGUAGE_PROMPT_DISMISS_KEY)
  const hasDismissedToday = dismissedDate === getNaturalDayKey()
  showLanguagePrompt.value = Boolean(
    resolvedBrowserLocale && resolvedBrowserLocale !== locale && !hasDismissedToday && !isNewsDetailRoute.value,
  )
})

onUnmounted(() => {
  if (navSearchCollapseTimer) {
    clearTimeout(navSearchCollapseTimer)
  }
  if (navSearchDebounceTimer) {
    clearTimeout(navSearchDebounceTimer)
  }
  if (navMeasureFrame !== null) {
    window.cancelAnimationFrame(navMeasureFrame)
    navMeasureFrame = null
  }
  navResizeObserver?.disconnect()
  navResizeObserver = null
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleDocumentClick)
  clearDownloadPopoverHideTimer()
})

const getExchangeStatus = (exchange: TraderListItem) => {
  return exchange?.sortedLabel?.find((item) => item?.labelType === 107)
}
const getExchangeYears = (exchange: TraderListItem) => {
  return exchange?.sortedLabel?.find((item) => item?.labelType === 101)
}
</script>

<template>
  <div
    class="pc-top-nav"
    :class="{
      'pc-top-nav-solid': props.forceSolidBackground,
      'pc-top-nav-opaque': props.forceSolidBackground || scrollProgress >= 1,
    }"
    :style="{
      '--scroll-progress': scrollProgress,
    }"
  >
    <div ref="topNavContentRef" class="pc-top-nav-content w-[clamp(800px,calc(100%-64px),1200px)]">
      <a ref="topNavLeftRef" class="pc-top-nav-content-left" :href="localePath('/')">
        <CommonImage
          v-show="!showExchangeTransparentLogo"
          src="https://resources1.interface003.com/web30/2026/05/19/1080ebf2-55c4-443c-9864-79be0a8107e7.svg"
          alt="logo"
          class="logo"
          loading="eager"
          object-fit="contain"
        />
        <CommonImage
          v-show="showExchangeTransparentLogo"
          src="https://resources1.interface003.com/web30/2026/06/17/67f3900d-85e5-4d0b-bb1d-684402090695.png"
          alt="logo"
          class="logo"
          loading="eager"
          object-fit="contain"
        />
      </a>
      <div
        class="pc-top-nav-content-right"
        :style="
          resolvedIsExchangeRoute ? { '--exchange-nav-hover-color': props.ultra ? '#fff1cb' : '#cfe1fd' } : undefined
        "
      >
        <div ref="navMeasureRef" class="pc-top-nav-content-menu pc-top-nav-content-menu-measure" aria-hidden="true">
          <div
            v-for="item in BASE_NAV_ITEMS"
            :key="`measure-${item.key}`"
            class="pc-top-nav-content-menu-item"
            data-nav-measure-item
            :class="{ active: item.active }"
          >
            <span class="pc-top-nav-content-menu-link">
              <span>{{ item.label }}</span>
              <ArrowDownIcon v-if="item.hasArrow" class="arrow-icon" />
            </span>
          </div>
        </div>
        <div class="pc-top-nav-content-menu" :class="{ 'is-exchange-route': resolvedIsExchangeRoute }">
          <div
            v-for="(item, index) in navItems"
            :key="item.key"
            class="pc-top-nav-content-menu-item"
            @click="homeTop(item.key)"
            :class="{ active: item.active }"
          >
            <button v-if="item.hasArrow" type="button" class="pc-top-nav-content-menu-link" aria-haspopup="menu">
              <span>{{ item.label }}</span>
              <ArrowDownIcon class="arrow-icon" />
            </button>
            <NuxtLink
              v-else
              :to="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="pc-top-nav-content-menu-link"
            >
              <span>{{ item.label }}</span>
            </NuxtLink>
            <div v-if="item.hasArrow && item.children && item.children.length > 0" class="sub-menu">
              <NuxtLink
                v-for="(child, cIndex) in item.children"
                :key="cIndex"
                :to="child.href"
                target="_blank"
                rel="noopener noreferrer"
                class="sub-menu-item"
                :class="{ 'has-bottom-border': child.hasBottom }"
                @click="homeRank(child)"
              >
                {{ child.label }}
              </NuxtLink>
            </div>
          </div>
        </div>
        <div ref="navActionRef" class="pc-top-nav-content-action">
          <div
            v-show="!isLoggedIn"
            data-auth-login
            data-allow-mismatch="style"
            style="display: none"
            class="login-entry-button"
            role="button"
            tabindex="0"
            @click="openLoginModal"
            @keydown.enter.prevent="openLoginModal"
            @keydown.space.prevent="openLoginModal"
          >
            {{ t('82902') }}
          </div>
          <div
            v-show="isLoggedIn"
            data-auth-user
            data-allow-mismatch="style"
            style="display: none"
            ref="userMenuRef"
            class="user-entry"
            @mouseenter="openUserMenu"
            @mouseleave="closeUserMenu"
            @click="homeTop('usercenter')"
          >
            <button
              class="user-entry-trigger"
              type="button"
              :aria-expanded="isUserMenuOpen"
              aria-haspopup="menu"
              @click.stop="toggleUserMenu"
            >
              <CommonImage
                v-show="Boolean(resolvedUserAvatar)"
                data-auth-avatar
                :src="resolvedUserAvatar"
                alt=""
                class="user-entry-avatar"
                loading="eager"
                fetch-priority="high"
              />
            </button>
            <div v-if="isUserMenuOpen" class="user-entry-dropdown">
              <a
                v-for="item in userMenuItems"
                :key="item.label"
                :href="item.href"
                :target="item.action === 'logout' ? undefined : '_blank'"
                :rel="item.action === 'logout' ? undefined : 'noopener noreferrer'"
                class="user-entry-dropdown-item"
                :class="{ danger: item.danger }"
                @click="handleUserMenuItemClick($event, item)"
              >
                {{ item.label }}
              </a>
            </div>
          </div>
          <div class="divider"></div>
          <div
            v-for="(item, index) in actionButtons"
            :key="index"
            class="action-button-wrapper"
            @click="homeTop(item.label)"
            :class="{
              'search-expanded': item.label === 'Search' && isNavSearchExpanded,
            }"
          >
            <!-- Search 按钮：展开态（搜索输入框） -->
            <div
              v-if="item.label === 'Search'"
              class="nav-search-bar"
              :class="{ 'is-expanded': isNavSearchExpanded }"
              @click="handleNavSearchExpand"
            >
              <SearchIcon
                v-if="isNavSearchExpanded"
                class="nav-search-icon-image ml-[12px] h-[24px] w-[24px] rtl:mr-[12px] rtl:ml-0"
                @click.stop="handleNavSearchSubmit"
              />
              <input
                id="navSearchInputRef"
                v-model="navSearchQuery"
                type="search"
                name="wikibitNavSearch"
                autocomplete="off"
                inputmode="search"
                class="nav-search-input"
                @focus="handleNavSearchFocus"
                @blur="handleNavSearchBlur"
                @keydown.enter.prevent="handleNavSearchSubmit"
              />
              <div class="nav-search-icon" @click.stop="handleNavSearchSubmit">
                <SearchIcon
                  v-if="!isNavSearchExpanded"
                  class="nav-search-icon-image"
                  @click.stop="handleNavSearchSubmit"
                />
                <CommonImage
                  v-else-if="navSearchQuery"
                  src="https://resources1.interface003.com/web30/2026/06/26/020c0828-d5bd-4293-a244-718628634c54.png"
                  :alt="item.label"
                  class="nav-search-icon-image cursor-pointer"
                  loading="lazy"
                  object-fit="contain"
                  @click.stop="navSearchQuery = ''"
                />
              </div>
            </div>
            <div v-if="item.label === 'Search' && showNavSearchDropdown" class="nav-search-dropdown" @mousedown.prevent>
              <div v-if="!showNavSearchEmpty" class="search-dropdown-content">
                <div v-if="!navSearchKeyword" class="search-dropdown-section search-history-section">
                  <div v-if="hasNavSearchHistory" class="search-dropdown-header">
                    <span class="search-dropdown-title">{{ t('80356') }}</span>
                    <button
                      class="search-history-delete"
                      type="button"
                      aria-label="Clear search history"
                      :disabled="!hasNavSearchHistory"
                      @click.stop="handleClearNavSearchHistory"
                    >
                      <DeleteIcon class="search-history-delete-icon" aria-hidden="true" />
                    </button>
                  </div>
                  <div v-if="hasNavSearchHistory" class="search-history-tags">
                    <div
                      v-for="(history, historyIndex) in navSearchHistory"
                      v-show="isNavHistoryExpanded || historyIndex < maxNavVisibleIndex"
                      :key="`${history.label}-${historyIndex}`"
                      class="search-history-tag"
                      @click="handleSelectNavSearchHistory(history)"
                    >
                      <CommonImage
                        v-if="history.logo"
                        :src="history.logo"
                        :alt="history.label"
                        class="search-history-tag-logo"
                        loading="lazy"
                        object-fit="cover"
                      />
                      <span class="search-history-tag-label">{{ history.label }}</span>
                      <span
                        class="search-history-tag-delete"
                        role="button"
                        style="display: block"
                        :aria-label="`Delete ${history.label} from search history`"
                        @click.stop="handleDeleteNavSearchHistory(historyIndex)"
                      >
                        <CloseIcon class="search-history-tag-delete-icon" aria-hidden="true" />
                      </span>
                    </div>
                    <div
                      v-if="hasNavMoreThanTwoLines"
                      class="search-history-expand"
                      :class="{ 'is-expanded': isNavHistoryExpanded }"
                      @click="isNavHistoryExpanded = !isNavHistoryExpanded"
                    >
                      <ArrowDownIcon class="search-history-expand-icon" />
                    </div>
                  </div>
                </div>
                <div
                  v-if="
                    navSearchHistory.length > 0 &&
                    (navSearchDefaultData?.globalRank?.length ?? 0) > 0 &&
                    !navSearchKeyword
                  "
                  class="search-dropdown-divider"
                ></div>

                <div class="search-dropdown-section popular-recommendation-section">
                  <div
                    v-if="!navSearchKeyword && (navSearchDefaultData?.globalRank?.length ?? 0) > 0"
                    class="search-dropdown-header"
                  >
                    <span class="search-dropdown-title">{{ t('80357') }}</span>
                  </div>
                  <div class="popular-recommendation-list">
                    <div
                      v-for="recommendation in navPopularRecommendations"
                      :key="recommendation.name"
                      class="popular-recommendation-item"
                      @click="handleSelectNavRecommendation(recommendation)"
                    >
                      <div class="popular-recommendation-main">
                        <div
                          class="popular-recommendation-logo"
                          :style="{
                            backgroundColor: recommendation.icon ? 'transparent' : '#25262B',
                          }"
                        >
                          <CommonImage
                            v-if="recommendation.icon"
                            :src="recommendation.icon"
                            :alt="recommendation.name"
                            class="popular-recommendation-logo-image"
                            loading="lazy"
                            object-fit="cover"
                          />
                          <span v-else class="popular-recommendation-logo-fallback">
                            {{ recommendation?.name?.[0]?.toUpperCase() }}
                          </span>
                        </div>
                        <div class="popular-recommendation-meta">
                          <div class="popular-recommendation-name">
                            {{ recommendation.name }}
                          </div>
                          <div v-if="recommendation.status || recommendation.years" class="popular-recommendation-tags">
                            <span
                              v-if="getExchangeStatus(recommendation)"
                              :style="{ color: formatColor(getExchangeStatus(recommendation)?.fontColor) }"
                              class="status-tag"
                            >
                              {{ getExchangeStatus(recommendation)?.content }}
                            </span>
                            <span
                              v-if="recommendation.status && recommendation.years"
                              class="divider-line"
                              aria-hidden="true"
                            ></span>
                            <span
                              v-if="getExchangeYears(recommendation)?.content"
                              :style="{
                                color: formatColor(getExchangeYears(recommendation)?.fontColor),
                              }"
                              class="years-text"
                              >{{ getExchangeYears(recommendation)?.content }}</span
                            >
                          </div>
                        </div>
                      </div>
                      <div v-if="recommendation.score" class="popular-recommendation-score">
                        <StarIcon />
                        <span class="score-text">{{ formatScoreValue(recommendation.score) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="search-dropdown-empty">
                <div>
                  <CommonImage
                    class="mx-auto block h-[130px] w-[230px]"
                    src="https://resources1.interface003.com/web30/2026/05/26/85c4961e-6d37-43cb-af78-7fbdce783fe5.png"
                    alt=""
                    loading="lazy"
                    object-fit="contain"
                  />
                  <div class="mt-[16px] text-[16px] text-[#292A2E]">{{ t('80359') }}</div>
                </div>
              </div>
            </div>

            <!-- 非 Search 按钮或 Search 收起态 -->
            <div
              v-if="item.label !== 'Search'"
              class="action-button"
              :class="{ 'cursor-pointer': item.label === 'Support' || item.label === 'Download' }"
              @mouseenter="
                item.label === 'Language'
                  ? handleLanguageButtonEnter()
                  : item.label === 'Download'
                    ? handleDownloadButtonEnter()
                    : undefined
              "
              @mouseleave="
                item.label === 'Language'
                  ? handleLanguageButtonLeave()
                  : item.label === 'Download'
                    ? handleDownloadButtonLeave()
                    : undefined
              "
              @click="item.label === 'Support' ? handleSupportClick() : undefined"
            >
              <GlobalIcon v-if="item.label === 'Language'" class="action-icon" />
              <DownloadIcon v-if="item.label === 'Download'" class="action-icon" />
              <ServiceIcon v-if="item.label === 'Support'" class="action-icon" />

              <!-- <CommonImage
                :src="item.icon"
                :alt="item.label"
                class="action-icon"
                loading="lazy"
                object-fit="contain"
              /> -->
            </div>

            <div
              v-if="item.label === 'Download'"
              class="download-popover"
              :class="{ visible: isDownloadPopoverVisible }"
              @mouseenter="handleDownloadButtonEnter"
              @mouseleave="handleDownloadButtonLeave"
            >
              <div class="download-popover-qr relative">
                <CommonQrCode v-if="downloadQrUrl" :text="downloadQrUrl" :size="102" class="h-full w-full" />
                <CommonImage
                  v-else
                  src="https://resources1.interface003.com/web30/2026/06/30/fc199559-2afb-4482-b1b6-00454fa931cc.png"
                  alt="QR Code"
                  class="h-full w-full"
                  loading="lazy"
                  object-fit="contain"
                />
                <div v-if="downloadQrUrl" class="absolute inset-0 m-auto size-[35px]">
                  <CommonImage
                    src="https://resources1.interface003.com/web30/2026/07/10/fa4fa0b9-b463-4604-be99-8dcaca416188.svg"
                    alt="logo"
                    class="size-full"
                    loading="lazy"
                  />
                </div>
              </div>
              <div class="download-popover-info">
                <div class="download-popover-title">{{ t('82711') }}</div>
                <div class="download-popover-desc">{{ t('82528') }}</div>
                <button type="button" class="download-popover-qr-btn" @click.prevent="handleDownloadClick">
                  {{ t('82490') }}
                </button>
              </div>
            </div>
            <div
              v-if="item.label === 'Language'"
              class="language-popover"
              :class="{ visible: isLanguagePopoverVisible }"
              @mouseenter="handleLanguageButtonEnter"
              @mouseleave="handleLanguageButtonLeave"
            >
              <div class="language-popover-title">{{ t('82390') }}</div>
              <div class="language-popover-grid">
                <a
                  v-for="(lang, lIndex) in locales"
                  :key="lIndex"
                  :href="switchLocalePath(lang.code)"
                  class="language-item"
                  :class="{ active: lang.code === locale }"
                  @click="handleLanguageItemClick(lang.code)"
                >
                  <span>{{ lang.name }}</span>
                  <svg
                    v-if="lang.code === locale"
                    class="check-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13.3333 4L5.99992 11.3333L2.66658 8"
                      stroke="#3D3D3D"
                      stroke-width="1.33333"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </a>
              </div>
            </div>
            <div v-if="item.label === 'Language' && showLanguagePrompt" class="language-switch-tip">
              <div class="language-switch-tip-text">{{ languagePromptText }}</div>
              <div class="language-switch-tip-actions">
                <button type="button" class="language-switch-tip-confirm" @click.prevent="handleSwitchToBrowserLocale">
                  {{ languagePromptConfirmText }}
                </button>
                <button
                  type="button"
                  class="language-switch-tip-close"
                  aria-label="Close language switch prompt"
                  @click.prevent="handleCloseLanguagePrompt"
                >
                  <CommonImage
                    src="https://resources1.interface003.com/web30/2026/06/22/3d446d3d-d161-41af-b055-bbb94f825f75.png"
                    alt=""
                    class="h-[20px] w-[20px]"
                    srcset=""
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="showLogoutConfirmDialog"
    class="logout-confirm-mask"
    role="presentation"
    @click.self="closeLogoutConfirmDialog"
  >
    <section
      class="logout-confirm-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logoutConfirmTitle"
      aria-describedby="logoutConfirmDescription"
    >
      <header class="logout-confirm-header">
        <h2 id="logoutConfirmTitle" class="logout-confirm-title">{{ t('82396') }}</h2>
        <button
          class="logout-confirm-close"
          type="button"
          aria-label="关闭退出登录确认弹窗"
          @click="closeLogoutConfirmDialog('关闭')"
        >
          <CloseIcon class="logout-confirm-close-icon" aria-hidden="true" />
        </button>
      </header>
      <div class="logout-confirm-content">
        <p id="logoutConfirmDescription" class="logout-confirm-description">
          {{ t('82397') }}
        </p>
      </div>
      <footer class="logout-confirm-footer">
        <button
          class="logout-confirm-button logout-confirm-button-cancel"
          type="button"
          @click="closeLogoutConfirmDialog('取消')"
        >
          {{ t('82398') }}
        </button>
        <button class="logout-confirm-button logout-confirm-button-submit" type="button" @click="confirmLogout">
          {{ t('82399') }}
        </button>
      </footer>
    </section>
  </div>
  <ClientOnly>
    <LazyLogin v-if="appStore.loginShow" />
  </ClientOnly>
</template>
<style scoped lang="less">
@media screen and (max-width: 1200px) {
  .pc-top-nav {
    .pc-top-nav-content-menu {
      gap: 16px !important;
    }
    .pc-top-nav-content-action {
      gap: 16px !important;
    }
  }
}
.pc-top-nav {
  --exchange-icon-color: rgb(
    calc(255 - 214 * var(--scroll-progress, 0)),
    calc(255 - 213 * var(--scroll-progress, 0)),
    calc(255 - 209 * var(--scroll-progress, 0))
  );

  width: calc(100% - var(--wikibit-scrollbar-compensation, 0px));
  min-width: 800px;
  height: 70px;
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  background-color: rgba(255, 255, 255, var(--scroll-progress, 0));
  border-bottom: 1px solid rgba(5, 21, 36, calc(0.06 * var(--scroll-progress, 0)));
  transition:
    background-color 0.15s ease-out,
    border-bottom-color 0.15s ease-out;

  &.pc-top-nav-solid {
    background-color: #fff;
    border-bottom-color: rgba(5, 21, 36, 0.06);
  }

  .pc-top-nav-content {
    height: 100%;
    margin: 0 auto;
    display: flex;
    column-gap: 38px;
    justify-content: space-between;
  }

  .pc-top-nav-content-left {
    flex: 0 0 139px;
    display: flex;
    align-items: center;
    width: 139px;

    .logo {
      flex-shrink: 0;
      width: 139px;
      height: 34px;
    }
  }

  .pc-top-nav-content-right {
    display: flex;
    align-items: center;
    height: 100%;

    .pc-top-nav-content-menu {
      display: flex;
      align-items: center;
      gap: 30px;
      height: 100%;

      &.pc-top-nav-content-menu-measure {
        position: absolute;
        width: max-content;
        visibility: hidden;
        pointer-events: none;
      }

      &.is-exchange-route {
        .pc-top-nav-content-menu-item,
        .pc-top-nav-content-menu-item.active {
          color: #fff;
        }

        .pc-top-nav-content-menu-item:hover {
          color: var(--exchange-nav-hover-color, #cfe1fd);

          .arrow-icon {
            :deep(path) {
              fill: var(--exchange-nav-hover-color, #cfe1fd);
            }
          }
        }

        .pc-top-nav-content-menu-item.active::after {
          background-color: #fff;
        }

        .arrow-icon {
          :deep(path) {
            fill: var(--exchange-icon-color);
          }
        }
      }

      .pc-top-nav-content-menu-item {
        color: #292a2e;
        font-family: Outfit;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        cursor: pointer;
        height: 100%;
        display: flex;
        flex-shrink: 0;
        align-items: center;
        position: relative;
        transition:
          color 0.2s ease,
          font-weight 0.2s ease;

        .pc-top-nav-content-menu-link {
          display: flex;
          align-items: center;
          height: 100%;
          padding: 0;
          border: none;
          background: transparent;
          color: inherit;
          font: inherit;
          text-decoration: none;
          flex-shrink: 0;
          white-space: nowrap;
          cursor: pointer;
        }

        .arrow-icon {
          width: 20px;
          height: 20px;
          margin-inline-start: 4px;
          transition: transform 0.25s ease;
        }

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 28px;
          height: 4px;
          background-color: #112d73;
          border-radius: 2px 2px 2px 2px;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .sub-menu {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          padding: 8px 0;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 8px 24px 0 rgba(0, 0, 0, 0.1),
            0 16px 48px 0 rgba(0, 0, 0, 0.1);
          color: #3d3d3d;
          font-family: Outfit;
          font-size: 14px;
          font-weight: 400;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          min-width: max-content;
          cursor: default;

          &::before {
            content: '';
            position: absolute;
            top: -20px;
            left: 0;
            width: 100%;
            height: 20px;
            background: transparent;
          }

          .sub-menu-item {
            padding: 12px 24px;
            cursor: pointer;
            transition: background-color 0.2s;
            text-align: start;
            color: inherit;
            text-decoration: none;
            white-space: nowrap;

            &:hover {
              background-color: #f5f5f5;
            }

            position: relative;
            &.has-bottom-border {
              &::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 24px;
                right: 24px;
                height: 1px;
                background-color: rgba(5, 21, 36, 0.06);
              }
            }
          }
        }

        &:hover {
          color: #112d73;

          .arrow-icon {
            transform: rotate(180deg);

            :deep(path) {
              fill: #112d73;
            }
          }

          .sub-menu {
            opacity: 1;
            visibility: visible;
          }
        }

        &.active {
          color: #112d73;
          font-weight: 600;

          &::after {
            opacity: 1;
          }
        }
      }
    }

    .pc-top-nav-content-action {
      display: flex;
      align-items: center;
      gap: 20px;
      .login-entry-button,
      .user-entry {
        margin-inline-start: 20px;
      }

      .login-entry-button {
        min-width: 85px;
        height: 32px;
        padding: 0 20px;
        border-radius: 9999px;
        background: #112d73;
        color: #fff;
        font-family: Outfit, sans-serif;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        cursor: pointer;
        user-select: none;
        transition:
          background-color 0.2s ease,
          transform 0.2s ease,
          box-shadow 0.2s ease;

        &:hover {
          background: #193c93;
          box-shadow: 0 8px 20px 0 rgba(17, 45, 115, 0.18);
        }

        &:focus-visible {
          outline: 2px solid rgba(17, 45, 115, 0.22);
          outline-offset: 2px;
        }
      }

      .user-entry {
        position: relative;
        display: flex;
        align-items: center;
      }

      .user-entry-trigger {
        width: 38px;
        height: 38px;
        padding: 0;
        border: none;
        border-radius: 50%;
        background: #f8f8f8;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        transition:
          background-color 0.2s ease,
          box-shadow 0.2s ease,
          transform 0.2s ease;

        &:hover {
          background: #f2f4f7;
          transform: translateY(-1px);
        }

        &:focus-visible {
          outline: 2px solid rgba(17, 45, 115, 0.18);
          outline-offset: 2px;
        }
      }

      .user-entry-avatar {
        width: 100%;
        height: 100%;
        flex-shrink: 0;
        border-radius: 50%;
        object-fit: cover;
        background: #d9e2f4;
      }

      .user-entry-avatar-fallback {
        color: #112d73;
        font-family: Outfit, sans-serif;
        font-size: 16px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .user-entry-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        z-index: 30;
        min-width: 188px;
        padding: 8px 0;
        border-radius: 8px;
        background: #fff;
        box-shadow:
          0 8px 24px 0 rgba(0, 0, 0, 0.1),
          0 16px 48px 0 rgba(0, 0, 0, 0.1);

        &::before {
          content: '';
          position: absolute;
          top: -20px;
          left: 0;
          width: 100%;
          height: 20px;
          background: transparent;
        }
      }

      .user-entry-dropdown-item {
        display: flex;
        align-items: center;
        padding: 12px 24px;
        color: #3d3d3d;
        font-family: Outfit, sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        text-decoration: none;
        transition:
          background-color 0.2s ease,
          color 0.2s ease;

        &:hover {
          background: #f5f5f5;
          color: #112d73;
        }

        &.danger:hover {
          color: #d14343;
        }
      }

      .divider {
        width: 1px;
        height: 24px;
        background-color: rgba(11, 18, 40, 0.14);
      }

      .action-button-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .action-button {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: #f8f8f8;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          .action-icon {
            width: 24px;
            height: 24px;
            cursor: pointer;

            :deep(path) {
              fill: #292a2e;
              transition: fill 0.2s ease;
            }
          }

          &:hover {
            .action-icon {
              :deep(path) {
                fill: #112d73;
              }
            }
          }
        }

        /* === 导航搜索栏过渡 === */
        .nav-search-bar {
          display: flex;
          align-items: center;
          width: 38px;
          height: 38px;
          border-radius: 19px;
          background: #f8f8f8;
          overflow: hidden;
          cursor: pointer;
          box-sizing: border-box;
          transition:
            width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
            border-color 0.3s ease,
            background-color 0.3s ease,
            box-shadow 0.3s ease;

          .nav-search-input {
            flex: 1;
            height: 100%;
            border: none;
            outline: none;
            background: transparent;
            font-family: Outfit, sans-serif;
            font-size: 14px;
            color: #292a2e;
            padding: 0;
            opacity: 0;
            width: 0;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            transition:
              width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
              opacity 0.25s ease 0s,
              padding 0.4s cubic-bezier(0.25, 1, 0.3, 1);

            &::placeholder {
              color: #b7b9be;
              font-size: 14px;
            }
          }

          .nav-search-icon {
            width: 38px;
            height: 38px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition:
              width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
              background-color 0.2s ease,
              background 0.2s ease;

            &:hover {
              background: linear-gradient(0deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.03) 100%), var(--color-background-subtlest, #F8F8F8);
            }

            .nav-search-icon-image {
              width: 24px;
              height: 24px;
              transition: transform 0.3s ease;
            }
          }

          /* 展开态 */
          &.is-expanded {
            width: 176px;
            border-color: rgba(11, 18, 40, 0.2);
            background: #f8f8f8;
            cursor: text;

            .nav-search-input {
              opacity: 1;
              width: 85px;
              padding: 0 0 0 16px;
              padding-inline-start: 16px;
              padding-inline-end: 0;
              transition:
                width 0.4s cubic-bezier(0.25, 1, 0.3, 1),
                opacity 0.3s ease 0.15s,
                padding 0.4s cubic-bezier(0.25, 1, 0.3, 1);
            }

            .nav-search-icon {
              width: 48px;

              .nav-search-icon-image {
                width: 24px;
                height: 24px;
              }
            }
          }

          &.is-expanded:focus-within {
            border-color: #112d73;
            box-shadow: 0 0 0 2px rgba(17, 45, 115, 0.12);
          }
        }

        .nav-search-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          z-index: 20;
          width: 416px;
          max-height: 520px;
          padding: 32px 0px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 20px;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          box-shadow:
            0 0 1px 0 rgba(30, 31, 33, 0.12),
            0 0 8px 0 rgba(30, 31, 33, 0.16);

          .search-dropdown-content {
            width: 100%;
            min-height: 0;
            display: flex;
            flex: 1 1 auto;
            flex-direction: column;
            // gap: 20px;
          }

          .search-dropdown-empty {
            width: 100%;
            min-height: 0;
            display: flex;
            flex: 1 1 auto;
            align-items: center;
            justify-content: center;
          }

          .search-dropdown-section {
            width: 100%;
            display: flex;
            flex-direction: column;
          }

          .search-history-section {
            gap: 16px;
          }

          .popular-recommendation-section {
            gap: 4px;
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
          }

          .search-dropdown-header {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 32px;
          }

          .search-dropdown-title {
            color: #7d818a;
            font-family: Outfit, sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }

          .search-history-delete {
            width: 14px;
            height: 14px;
            padding: 0;
            border: none;
            background: transparent;
            color: #7d818a;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            .search-history-delete-icon {
              width: 14px;
            }

            &:hover:not(:disabled) {
              color: #ef4444;
            }

            &:disabled {
              opacity: 0.4;
              cursor: default;
            }
          }

          .search-history-tags {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 12px;
            // margin-bottom: 20px;
            padding: 0 32px;
          }

          .search-history-tag,
          .search-history-expand {
            height: 30px;
            padding: 4px 12px;
            border: none;
            border-radius: 1000px;
            background: #f8f8f8;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
            box-sizing: border-box;
            overflow: visible;
            color: #292a2e;
            font-family: Outfit, sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }

          .search-history-tag {
            max-width: 200px;
            cursor: pointer;
            transition:
              background-color 0.2s ease,
              color 0.2s ease;

            &:hover {
              background: #f1f3f6;
              position: relative;
              z-index: 1;
            }
          }

          .search-history-expand {
            width: 30px;
            padding: 0;
            cursor: pointer;
            transition: background-color 0.2s ease;

            .search-history-expand-icon {
              width: 20px;
              height: 20px;
              transition: transform 0.3s ease;
            }

            &.is-expanded {
              .search-history-expand-icon {
                transform: rotate(180deg);
              }
            }

            &:hover {
              background: #eaecef;
            }
          }

          .search-history-tag-logo {
            width: 20px;
            height: 20px;
            border-radius: 1000px;
            object-fit: cover;
            flex-shrink: 0;
          }

          .search-history-tag-label {
            display: inline-block;
            min-width: 0;
            height: 22px;
            line-height: 22px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .search-history-tag-delete {
            width: 14px;
            opacity: 1;
            padding: 0;
            border: none;
            background: transparent;
            color: #7d818a;
            overflow: visible;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: color 0.2s ease;

            .search-history-tag-delete-icon {
              display: block;
            }
          }

          .search-history-tag-delete:hover {
            color: #292a2e;
          }

          .search-history-empty {
            color: #b7b9be;
            font-family: Outfit, sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
          }

          .search-dropdown-divider {
            width: calc(100% - 64px);
            margin: 20px 32px;
            height: 1px;
            background: rgba(5, 21, 36, 0.06);
          }

          .popular-recommendation-list {
            width: 100%;
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
              display: none;
            }
          }

          .popular-recommendation-item {
            width: 100%;
            padding: 16px 32px;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            gap: 8px;
            border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
            cursor: pointer;

            &:last-child {
              border-bottom: none;
            }
          }
          .popular-recommendation-item:hover {
            background-color: #f8f8f8;
          }

          .popular-recommendation-main {
            flex: 1 1 0;
            min-width: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .popular-recommendation-logo {
            width: 32px;
            height: 32px;
            border-radius: 4px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .popular-recommendation-logo-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .popular-recommendation-logo-fallback {
            color: #fff;
            font-family: Outfit, sans-serif;
            font-size: 16px;
            font-weight: 600;
            line-height: 1;
          }

          .popular-recommendation-meta {
            flex: 1 1 0;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .popular-recommendation-name {
            color: #292a2e;
            font-family: Outfit, sans-serif;
            font-size: 18px;
            font-weight: 500;
            line-height: 26px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .popular-recommendation-tags {
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: Outfit, sans-serif;
            font-size: 12px;
            font-weight: 400;
            line-height: 18px;
          }

          .status-tag {
          }

          .divider-line {
            width: 1px;
            height: 8px;
            background: rgba(5, 21, 36, 0.12);
            flex-shrink: 0;
          }

          .years-text {
            color: #505258;
          }

          .popular-recommendation-score {
            flex-shrink: 0;
            padding: 4px 12px;
            border-radius: 1000px;
            background: #f8f8f8;
            display: flex;
            align-items: center;
            gap: 4px;

            svg {
              width: 14px;
              height: 14px;
            }

            .score-text {
              color: #292a2e;
              text-align: center;
              font-family: WikiFX;
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
            }
          }
        }

        .language-popover {
          position: absolute;
          top: calc(100% + 15px);
          inset-inline-end: -20px;
          display: flex;
          width: 801px;
          padding: 40px;
          flex-direction: column;
          align-items: flex-start;
          gap: 20px;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 8px 24px 0 rgba(0, 0, 0, 0.1),
            0 16px 48px 0 rgba(0, 0, 0, 0.1);

          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          cursor: default;

          &.visible {
            opacity: 1;
            visibility: visible;
          }

          &::before {
            content: '';
            position: absolute;
            top: -20px;
            left: 0;
            width: 100%;
            height: 20px;
            background: transparent;
          }

          .language-popover-title {
            color: #292a2e;
            font-family: Outfit;
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px;
          }

          .language-popover-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            row-gap: 20px;
            column-gap: 16px;
            width: 100%;
          }

          .language-item {
            text-decoration: none;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 12px;
            border-radius: 4px;
            color: #7d818a;
            font-family: Outfit;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 24px;
            cursor: pointer;
            transition:
              background-color 0.2s,
              color 0.2s;

            &:hover {
              background-color: #f5f5f5;
            }

            &.active {
              color: #292a2e;
              background-color: #f5f5f5;
              font-weight: 500;
            }

            .check-icon {
              flex-shrink: 0;
            }
          }
        }

        .download-popover {
          position: absolute;
          top: calc(100% + 15px);
          inset-inline-end: -20px;
          display: flex;
          padding: 16px;
          align-items: center;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 8px 24px 0 rgba(0, 0, 0, 0.1),
            0 16px 48px 0 rgba(0, 0, 0, 0.1);

          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: all 0.2s ease;
          cursor: default;
          gap: 16px;

          &.visible {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
          }

          &::before {
            content: '';
            position: absolute;
            top: -20px;
            left: 0;
            width: 100%;
            height: 20px;
            background: transparent;
          }

          .download-popover-qr {
            display: flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            width: 102px;
            height: 102px;
            overflow: hidden;

            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
          }

          .download-popover-info {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .download-popover-title {
            color: #292a2e;
            font-size: 18px;
            font-weight: 600;
            line-height: 26px;
            white-space: nowrap;
          }

          .download-popover-desc {
            color: #505258;
            font-size: 14px;
            font-weight: 500;
            line-height: 22px;
            white-space: nowrap;
          }
          .download-popover-qr-btn {
            background-color: #112d73;
            color: #ffffff;
            border-radius: 1000px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
            padding: 4px 20.5px;
            width: fit-content;
          }
        }
        .language-switch-tip {
          position: absolute;
          top: calc(100% + 15px);
          left: 50%;
          transform: translateX(-50%);
          z-index: 21;
          width: max-content;
          padding: 12px 20px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 0 1px 0 rgba(30, 31, 33, 0.12),
            0 0 8px 0 rgba(30, 31, 33, 0.16);
        }

        .language-switch-tip-text {
          color: #292a2e;
          font-size: 14px;
          font-weight: 400;
          width: 202px;
        }

        .language-switch-tip-actions {
          display: flex;
          align-items: center;

          gap: 16px;
          flex-shrink: 0;
        }

        .language-switch-tip-confirm {
          padding: 4px 16px;
          border: none;
          border-radius: 28px;
          background: #112d73;
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .language-switch-tip-confirm:hover {
          background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
        }

        .language-switch-tip-confirm:active {
          background: linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%), #112d73;
        }

        .language-switch-tip-close {
          padding: 0;
          border: none;
          background: transparent;
          color: #292a2e;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          gap: 16px;
        }

        .language-switch-tip-close-icon {
          width: 20px;
          height: 20px;
        }
      }
    }

    .pc-top-nav-content-menu.is-exchange-route + .pc-top-nav-content-action {
      .nav-search-bar {
        background: rgba(227, 228, 242, 0.12);

        .nav-search-input {
          color: #fff;

          &::placeholder {
            color: rgba(255, 255, 255, 0.72);
          }
        }

        .nav-search-icon-image {
          filter: brightness(0) invert(calc(1 - 0.84 * var(--scroll-progress, 0)));
        }
      }

      .action-button {
        background: rgba(227, 228, 242, 0.12);

        .action-icon {
          :deep(path) {
            fill: var(--exchange-icon-color);
          }
        }

        &:hover {
          .action-icon {
            :deep(path) {
              fill: var(--exchange-nav-hover-color, #cfe1fd);
            }
          }
        }
      }
    }
  }

  &.pc-top-nav-opaque {
    .pc-top-nav-content-menu.is-exchange-route {
      .pc-top-nav-content-menu-item {
        color: #292a2e;
      }

      .pc-top-nav-content-menu-item.active {
        color: #112d73;

        &::after {
          background-color: #112d73;
        }
      }
    }

    .pc-top-nav-content-menu.is-exchange-route + .pc-top-nav-content-action {
      .nav-search-bar {
        background: #f8f8f8;

        .nav-search-input {
          color: #292a2e;

          &::placeholder {
            color: #b7b9be;
          }
        }

        .nav-search-icon-image {
          filter: none;
        }
      }

      .action-button {
        background: #f8f8f8;

        .action-icon {
          :deep(path) {
            fill: #292a2e;
          }
        }

        &:hover {
          .action-icon {
            :deep(path) {
              fill: #112d73;
            }
          }
        }
      }
    }
  }
}

.logout-confirm-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.32);
}

.logout-confirm-dialog {
  width: 440px;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.2),
    0 8px 20px 0 rgba(0, 0, 0, 0.12);
}

.logout-confirm-header {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
}

.logout-confirm-title {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.85);
  font-family: 'PingFang SC', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-confirm-close {
  width: 24px;
  height: 24px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #292a2e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.logout-confirm-close-icon {
  width: 16px;
  height: 16px;
}

.logout-confirm-content {
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px;
  background: #fff;
}

.logout-confirm-description {
  width: 100%;
  margin: 0;
  color: rgba(0, 0, 0, 0.7);
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.logout-confirm-footer {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 24px;
  background: #fff;
}

.logout-confirm-button {
  height: 46px;
  padding: 12px 20px;
  border: none;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease;
}

.logout-confirm-button-cancel {
  background: #f8f8f8;
  color: #292a2e;
}

.logout-confirm-button-submit {
  background: #112d73;
  color: #fff;
}

.logout-confirm-button-submit:hover {
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), #112d73;
}

.logout-confirm-button-submit:active {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%), #112d73;
}
html[dir='rtl'] {
  .nav-search-dropdown {
    right: auto;
    left: 0;
    inset-inline-start: auto;
    inset-inline-end: 0;
  }
}
</style>
